import { useCallback, useEffect, useMemo, useState } from 'react';

import { useBridgewayApi } from '@/api/BridgewayApi';
import { useUserId } from '@/auth/UserIdContext';
import { logger } from '@/logger';

import type { Artifact } from '../../../../../bridge/types/artifact.ts';

type Group = {
	parent: Artifact | null;
	children: Artifact[];
};

function groupByParent(items: Artifact[]): Group[] {
	const byId = new Map<string, Artifact>();
	for (const a of items) byId.set(a.id, a);

	const childrenByParent = new Map<string, Artifact[]>();
	const topLevel: Artifact[] = [];

	for (const a of items) {
		const parentId = a.process_id;
		if (parentId && byId.has(parentId)) {
			const bucket = childrenByParent.get(parentId) ?? [];
			bucket.push(a);
			childrenByParent.set(parentId, bucket);
		} else {
			topLevel.push(a);
		}
	}

	topLevel.sort(
		(a, b) =>
			new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
	);

	return topLevel.map((parent) => ({
		parent,
		children: (childrenByParent.get(parent.id) ?? []).sort(
			(a, b) =>
				new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
		),
	}));
}

const STATUS_COLORS: Record<string, string> = {
	draft: '#ca8a04',
	active: '#10b981',
	completed: '#3b82f6',
	paused: '#6b7280',
};

const STATUS_FILTERS = [
	'all',
	'draft',
	'active',
	'completed',
	'paused',
] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];

export const DemoArtifacts = () => {
	const userId = useUserId();
	const bridgewayApi = useBridgewayApi();
	const [items, setItems] = useState<Artifact[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
	const [typeFilter, setTypeFilter] = useState<string>('all');
	const [expanded, setExpanded] = useState<Record<string, boolean>>({});

	const fetchItems = useCallback(async () => {
		if (!userId) return;
		setLoading(true);
		setError(null);
		try {
			const res = await bridgewayApi.get<Artifact[]>(
				`/api/user/${userId}/artifacts/`,
			);
			setItems(Array.isArray(res) ? res : []);
		} catch (err) {
			logger.error('artifacts fetch failed', err);
			setError(err instanceof Error ? err.message : String(err));
		} finally {
			setLoading(false);
		}
	}, [userId, bridgewayApi]);

	useEffect(() => {
		void fetchItems();
	}, [fetchItems]);

	const availableTypes = useMemo(() => {
		const set = new Set(items.map((a) => a.type));
		return ['all', ...Array.from(set).sort()];
	}, [items]);

	const filtered = useMemo(() => {
		return items.filter((a) => {
			if (statusFilter !== 'all' && a.status !== statusFilter) return false;
			if (typeFilter !== 'all' && a.type !== typeFilter) return false;
			return true;
		});
	}, [items, statusFilter, typeFilter]);

	const groups = useMemo(() => groupByParent(filtered), [filtered]);

	const toggle = (id: string) =>
		setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

	return (
		<div style={{ padding: '1.5rem', maxWidth: 1200, margin: '0 auto' }}>
			<div
				style={{
					display: 'flex',
					alignItems: 'baseline',
					justifyContent: 'space-between',
					marginBottom: '1rem',
				}}
			>
				<h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>
					Artifacts
					<span
						style={{
							marginLeft: '0.5rem',
							fontSize: '0.875rem',
							fontWeight: 500,
							color: '#6b7280',
						}}
					>
						{filtered.length} / {items.length}
					</span>
				</h1>
				<button
					type="button"
					onClick={() => void fetchItems()}
					style={{
						padding: '0.375rem 0.875rem',
						borderRadius: 6,
						border: '1px solid #d1d5db',
						background: '#fff',
						fontSize: '0.8125rem',
						cursor: 'pointer',
					}}
				>
					Refresh
				</button>
			</div>

			<div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
				<label style={{ fontSize: '0.8125rem' }}>
					Status:{' '}
					<select
						value={statusFilter}
						onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
					>
						{STATUS_FILTERS.map((s) => (
							<option key={s} value={s}>
								{s}
							</option>
						))}
					</select>
				</label>
				<label style={{ fontSize: '0.8125rem' }}>
					Type:{' '}
					<select
						value={typeFilter}
						onChange={(e) => setTypeFilter(e.target.value)}
					>
						{availableTypes.map((t) => (
							<option key={t} value={t}>
								{t}
							</option>
						))}
					</select>
				</label>
			</div>

			{error && (
				<div
					style={{
						padding: '0.75rem',
						background: '#fef2f2',
						color: '#dc2626',
						borderRadius: 6,
						marginBottom: '1rem',
					}}
				>
					{error}
				</div>
			)}

			{loading && items.length === 0 && (
				<div style={{ color: '#6b7280' }}>Loading...</div>
			)}

			{groups.length === 0 && !loading && (
				<div style={{ color: '#6b7280' }}>No artifacts match.</div>
			)}

			{groups.map(({ parent, children }) =>
				parent ? (
					<GroupCard
						key={parent.id}
						parent={parent}
						childItems={children}
						expanded={expanded}
						onToggle={toggle}
					/>
				) : null,
			)}
		</div>
	);
};

function GroupCard({
	parent,
	childItems,
	expanded,
	onToggle,
}: {
	parent: Artifact;
	childItems: Artifact[];
	expanded: Record<string, boolean>;
	onToggle: (id: string) => void;
}) {
	return (
		<div
			style={{
				border: '1px solid #e5e7eb',
				borderRadius: 8,
				marginBottom: '1rem',
				background: '#fff',
			}}
		>
			<ArtifactRow
				artifact={parent}
				isExpanded={!!expanded[parent.id]}
				onToggle={() => onToggle(parent.id)}
				depth={0}
				childCount={childItems.length}
			/>
			{childItems.map((c) => (
				<ArtifactRow
					key={c.id}
					artifact={c}
					isExpanded={!!expanded[c.id]}
					onToggle={() => onToggle(c.id)}
					depth={1}
				/>
			))}
		</div>
	);
}

function ArtifactRow({
	artifact,
	isExpanded,
	onToggle,
	depth,
	childCount,
}: {
	artifact: Artifact;
	isExpanded: boolean;
	onToggle: () => void;
	depth: number;
	childCount?: number;
}) {
	const statusColor = STATUS_COLORS[artifact.status] ?? '#6b7280';
	return (
		<div
			style={{
				borderTop: depth === 0 ? 'none' : '1px solid #f3f4f6',
				padding: '0.75rem 1rem',
				paddingLeft: `${1 + depth * 1.5}rem`,
				background: depth === 0 ? '#f9fafb' : '#fff',
			}}
		>
			<button
				type="button"
				onClick={onToggle}
				style={{
					width: '100%',
					display: 'flex',
					alignItems: 'center',
					gap: '0.75rem',
					background: 'none',
					border: 'none',
					padding: 0,
					textAlign: 'left',
					cursor: 'pointer',
				}}
			>
				<span
					style={{
						fontSize: '0.875rem',
						width: '0.75rem',
						color: '#6b7280',
					}}
				>
					{isExpanded ? '▼' : '▶'}
				</span>
				<span
					style={{
						fontFamily: 'ui-monospace, SFMono-Regular, monospace',
						fontSize: '0.8125rem',
						fontWeight: 600,
						color: '#111827',
					}}
				>
					{artifact.type}
				</span>
				<span
					style={{
						padding: '2px 8px',
						borderRadius: 4,
						background: `${statusColor}15`,
						color: statusColor,
						fontSize: '0.6875rem',
						fontWeight: 600,
					}}
				>
					{artifact.status}
				</span>
				{typeof childCount === 'number' && childCount > 0 && (
					<span
						style={{
							padding: '2px 8px',
							borderRadius: 4,
							background: '#eff6ff',
							color: '#3b82f6',
							fontSize: '0.6875rem',
							fontWeight: 600,
						}}
					>
						{childCount} {childCount === 1 ? 'child' : 'children'}
					</span>
				)}
				<span
					style={{
						fontFamily: 'ui-monospace, SFMono-Regular, monospace',
						fontSize: '0.6875rem',
						color: '#9ca3af',
					}}
				>
					{artifact.id.slice(0, 8)}
				</span>
				<span
					style={{
						marginLeft: 'auto',
						fontSize: '0.75rem',
						color: '#6b7280',
						maxWidth: '40%',
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
					}}
					title={artifact.description}
				>
					{artifact.description}
				</span>
			</button>

			{isExpanded && (
				<pre
					style={{
						marginTop: '0.5rem',
						padding: '0.75rem',
						background: '#0f172a',
						color: '#e2e8f0',
						borderRadius: 6,
						fontSize: '0.75rem',
						lineHeight: 1.5,
						overflow: 'auto',
						maxHeight: '500px',
					}}
				>
					{JSON.stringify(artifact, null, 2)}
				</pre>
			)}
		</div>
	);
}
